import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import api from "../lib/api";
import { Ornament } from "../components/Ornament";
import { Monogram } from "../components/Monogram";
import { UpgradeModal } from "../components/UpgradeModal";

// Order relations top-to-bottom by family generation
const ROWS = [
  { title: "Great-grandparents", relations: ["Great-grandparent"] },
  { title: "Grandparents",       relations: ["Grandparent"] },
  { title: "Parents · Aunts · Uncles · Partners", relations: ["Parent", "Aunt", "Uncle", "Partner"] },
  { title: "Siblings",           relations: ["Sibling"] },
  { title: "Children · Other",   relations: ["Child", "Other"] },
  { title: "Grandchildren",      relations: ["Grandchild"] },
];

export default function FamilyTree() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/tree");
        setData(res.data);
      } catch (e) {
        if (e?.response?.status === 402) {
          setUpgradeOpen(true);
        }
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center paper">
      <p className="italic text-[#8C8278]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.3rem" }}>Reflecting on your story…</p>
    </div>
  );

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center paper px-6" data-testid="tree-locked">
        <div className="max-w-lg text-center">
          <p className="label-ui text-[11px] text-[#B8973A] mb-3">The Family Tree</p>
          <h1 className="text-3xl sm:text-4xl mb-4" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            Part of The Living Book Family.
          </h1>
          <Ornament className="my-6" />
          <p className="italic text-[#8C8278] mb-8" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.15rem" }}>
            The Family Tree weaves every voice in your family into a single, visual heirloom — generations laid out side by side.
          </p>
          <button onClick={() => navigate("/subscribe?plan=family")} className="btn-gold" data-testid="tree-upgrade-btn">Unlock the Family Tree</button>
          <div className="mt-6">
            <Link to="/dashboard" className="label-ui text-[10px] text-[#8C8278] hover:text-[#111D33] transition-colors" data-testid="back-link">← Back</Link>
          </div>
        </div>
        <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} reason="chapter_locked" />
      </div>
    );
  }

  const rows = ROWS.map(r => ({
    ...r,
    items: data.profiles.filter(p => r.relations.includes(p.relation)),
  })).filter(r => r.items.length > 0);

  return (
    <div className="min-h-screen paper" data-testid="family-tree-page">
      <header className="max-w-6xl mx-auto px-6 sm:px-10 py-8 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2 label-ui text-[11px] text-[#8C8278] hover:text-[#111D33] transition-colors" data-testid="back-link">
          <ChevronLeft size={16} /> Family
        </Link>
        <div className="flex items-center gap-3">
          <span className="text-[#B8973A] text-lg" style={{ fontFamily: "Cormorant Garamond, serif" }}>✦</span>
          <span style={{ fontFamily: "Cormorant Garamond, serif", letterSpacing: "0.06em" }}>The Living Book</span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-10 py-10">
        <div className="text-center mb-16">
          <p className="label-ui text-[11px] text-[#B8973A]">The family tree</p>
          <h1 className="text-5xl sm:text-6xl mt-4" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 300 }}>
            {data.family_name}
          </h1>
          <Ornament className="my-6" />
          <p className="italic text-[#8C8278] max-w-2xl mx-auto" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.15rem" }}>
            Every voice in your family, laid out side by side. Tap a name to open their stories.
          </p>
        </div>

        <div className="space-y-16">
          {rows.map((row, idx) => (
            <div key={idx} className="text-center" data-testid={`tree-row-${idx}`}>
              <p className="label-ui text-[10px] text-[#B8973A] mb-6">{row.title}</p>
              <div className="flex flex-wrap items-start justify-center gap-6">
                {row.items.map(p => (
                  <button
                    key={p.profile_id}
                    onClick={() => navigate(`/app/profile/${p.profile_id}`)}
                    className="text-center bg-white hairline-cream hover:bg-[#F7F0E0] transition-colors duration-500 px-6 py-5 min-w-[160px]"
                    style={{ borderRadius: 4, border: p.is_memorial ? "1px solid #B8973A" : undefined }}
                    data-testid={`tree-profile-${p.profile_id}`}
                  >
                    <div className="flex justify-center mb-3"><Monogram name={p.name} size={56} /></div>
                    <p style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem" }}>{p.name}</p>
                    {p.is_memorial && (
                      <p className="label-ui text-[9px] text-[#B8973A] mt-1">In memory</p>
                    )}
                    <p className="text-xs text-[#8C8278] mt-1" style={{ fontFamily: "Georgia, serif" }}>
                      {p.story_count > 0 ? `${p.story_count} stor${p.story_count === 1 ? "y" : "ies"}` : "—"}
                    </p>
                  </button>
                ))}
              </div>
              {idx < rows.length - 1 && (
                <div className="flex justify-center mt-10">
                  <span className="w-px h-12" style={{ background: "#B8973A", opacity: 0.45 }} aria-hidden="true" />
                </div>
              )}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
